/** Admin portal date/time display preferences (Profile + Settings). */

export const DISPLAY_SETTINGS_KEY = 'adminDisplaySettings';

export const DATE_FORMAT_OPTIONS = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD',
  'DD MMM YYYY',
  'MMM DD, YYYY',
] as const;

export type DateFormatOption = (typeof DATE_FORMAT_OPTIONS)[number];
export type TimeFormatOption = '12h' | '24h';

export const DEFAULT_DATE_FORMAT: DateFormatOption = 'MM/DD/YYYY';
export const DEFAULT_TIME_FORMAT: TimeFormatOption = '12h';

export function isDateFormatOption(value: unknown): value is DateFormatOption {
  return typeof value === 'string' && (DATE_FORMAT_OPTIONS as readonly string[]).includes(value);
}

export function isTimeFormatOption(value: unknown): value is TimeFormatOption {
  return value === '12h' || value === '24h';
}

export function readStoredDateTimeFormats(): {
  dateFormat: DateFormatOption;
  timeFormat: TimeFormatOption;
} {
  try {
    const raw = localStorage.getItem(DISPLAY_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { dateFormat?: string; timeFormat?: string };
      return {
        dateFormat: isDateFormatOption(parsed.dateFormat) ? parsed.dateFormat : DEFAULT_DATE_FORMAT,
        timeFormat: isTimeFormatOption(parsed.timeFormat) ? parsed.timeFormat : DEFAULT_TIME_FORMAT,
      };
    }
  } catch {
    // ignore corrupt storage
  }
  return { dateFormat: DEFAULT_DATE_FORMAT, timeFormat: DEFAULT_TIME_FORMAT };
}

export function persistDateTimeFormats(
  dateFormat: DateFormatOption,
  timeFormat: TimeFormatOption
): void {
  try {
    const raw = localStorage.getItem(DISPLAY_SETTINGS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    localStorage.setItem(
      DISPLAY_SETTINGS_KEY,
      JSON.stringify({ ...parsed, dateFormat, timeFormat })
    );
  } catch {
    // ignore quota / private mode
  }
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Format a date with the user's preferred pattern (UTC calendar parts). */
export function formatWithDatePattern(
  value: string | Date | null | undefined,
  dateFormat: DateFormatOption = DEFAULT_DATE_FORMAT
): string {
  if (value == null || value === '') return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const mm = pad2(m + 1);
  const dd = pad2(d);
  const mon = MONTH_SHORT[m];

  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return `${dd}/${mm}/${y}`;
    case 'YYYY-MM-DD':
      return `${y}-${mm}-${dd}`;
    case 'DD MMM YYYY':
      return `${dd} ${mon} ${y}`;
    case 'MMM DD, YYYY':
      return `${mon} ${dd}, ${y}`;
    case 'MM/DD/YYYY':
    default:
      return `${mm}/${dd}/${y}`;
  }
}

export function formatWithTimePattern(
  value: string | Date | null | undefined,
  timeFormat: TimeFormatOption = DEFAULT_TIME_FORMAT
): string {
  if (value == null || value === '') return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
    timeZone: 'UTC',
  });
}
